import { z } from "zod";
import { ValidationError } from "../errors/ValidationError";
import { dispatchValidationError } from "../errors/errorDispatcher";

const isoDateString = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, "Expected YYYY-MM-DD")
  .optional()
  .nullable();

export const vendorTopProductsClickRequestSchema = z
  .object({
    vendorListId: z.number().int().positive().optional().nullable(),
    startDate: isoDateString,
    endDate: isoDateString,
    categoryListId: z.number().int().optional().nullable(),
    catalogListId: z.number().int().optional().nullable(),
    limit: z.number().int().min(1).max(100).optional().nullable(),
  })
  .superRefine((val, ctx) => {
    const hasStart = val.startDate != null && val.startDate !== "";
    const hasEnd = val.endDate != null && val.endDate !== "";
    if (hasStart !== hasEnd) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Provide both startDate and endDate, or omit both.",
      });
    }
    if (hasStart && hasEnd && val.startDate > val.endDate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "endDate must be on or after startDate.",
      });
    }
  });

const topProductByClickItemSchema = z.object({
  productListId: z.number().int().optional().nullable(),
  catalogListId: z.number().int().optional().nullable(),
  label: z.coerce.string(),
  clickCount: z.coerce.number(),
  imageUrls: z.array(z.string()).optional().default([]),
});

export const vendorTopProductsClickResponseSchema = z.object({
  items: z.array(topProductByClickItemSchema).optional().default([]),
  statusCode: z.number().optional(),
  message: z.string().optional(),
  status: z.unknown().optional(),
});

export function validateVendorTopProductsClickRequest(data) {
  const parsed = vendorTopProductsClickRequestSchema.safeParse(data);
  if (!parsed.success) {
    const message = `Invalid vendor top-products request: ${parsed.error.message}`;
    dispatchValidationError({ message, issues: parsed.error.issues, raw: parsed.error });
    throw new ValidationError(message, { issues: parsed.error.issues, raw: parsed.error });
  }
  return parsed.data;
}

export function validateVendorTopProductsClickResponse(data) {
  const parsed = vendorTopProductsClickResponseSchema.safeParse(data);
  if (!parsed.success) {
    const message = `Invalid vendor top-products response: ${parsed.error.message}`;
    dispatchValidationError({ message, issues: parsed.error.issues, raw: parsed.error });
    throw new ValidationError(message, { issues: parsed.error.issues, raw: parsed.error });
  }
  return parsed.data;
}

export const vendorTrafficRequestSchema = z
  .object({
    vendorListId: z.number().int().positive().optional().nullable(),
    resolution: z.enum(["day", "month"]).optional().nullable(),
    limit: z.number().int().min(1).max(120).optional().nullable(),
    startDate: isoDateString,
    endDate: isoDateString,
  })
  .superRefine((val, ctx) => {
    const hasStart = val.startDate != null && val.startDate !== "";
    const hasEnd = val.endDate != null && val.endDate !== "";
    if (hasStart !== hasEnd) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Provide both startDate and endDate for traffic, or omit both.",
      });
    }
    if (hasStart && hasEnd && val.startDate > val.endDate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "endDate must be on or after startDate.",
      });
    }
  });

const trafficPointSchema = z.object({
  period: z.string(),
  clickCount: z.coerce.number(),
  growthPercent: z.number().nullable().optional(),
});

export const vendorTrafficResponseSchema = z.object({
  resolution: z.string().optional(),
  points: z.array(trafficPointSchema).optional().default([]),
  statusCode: z.number().optional(),
  message: z.string().optional(),
  status: z.unknown().optional(),
});

export const vendorTopCategoriesByClickRequestSchema = z
  .object({
    vendorListId: z.number().int().positive().optional().nullable(),
    startDate: isoDateString,
    endDate: isoDateString,
    limit: z.number().int().min(1).max(100).optional().nullable(),
  })
  .superRefine((val, ctx) => {
    const hasStart = val.startDate != null && val.startDate !== "";
    const hasEnd = val.endDate != null && val.endDate !== "";
    if (hasStart !== hasEnd) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Provide both startDate and endDate, or omit both.",
      });
    }
    if (hasStart && hasEnd && val.startDate > val.endDate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "endDate must be on or after startDate.",
      });
    }
  });

const categoryClickItemSchema = z.object({
  categoryListId: z.number().int().optional().nullable(),
  label: z.coerce.string(),
  clickCount: z.coerce.number(),
});

export const vendorTopCategoriesByClickResponseSchema = z.object({
  items: z.array(categoryClickItemSchema).optional().default([]),
  statusCode: z.number().optional(),
  message: z.string().optional(),
  status: z.unknown().optional(),
});

export function validateVendorTrafficRequest(data) {
  const parsed = vendorTrafficRequestSchema.safeParse(data);
  if (!parsed.success) {
    const message = `Invalid vendor traffic request: ${parsed.error.message}`;
    dispatchValidationError({ message, issues: parsed.error.issues, raw: parsed.error });
    throw new ValidationError(message, { issues: parsed.error.issues, raw: parsed.error });
  }
  return parsed.data;
}

export function validateVendorTrafficResponse(data) {
  const parsed = vendorTrafficResponseSchema.safeParse(data);
  if (!parsed.success) {
    const message = `Invalid vendor traffic response: ${parsed.error.message}`;
    dispatchValidationError({ message, issues: parsed.error.issues, raw: parsed.error });
    throw new ValidationError(message, { issues: parsed.error.issues, raw: parsed.error });
  }
  return parsed.data;
}

export function validateVendorTopCategoriesByClickRequest(data) {
  const parsed = vendorTopCategoriesByClickRequestSchema.safeParse(data);
  if (!parsed.success) {
    const message = `Invalid vendor top-categories request: ${parsed.error.message}`;
    dispatchValidationError({ message, issues: parsed.error.issues, raw: parsed.error });
    throw new ValidationError(message, { issues: parsed.error.issues, raw: parsed.error });
  }
  return parsed.data;
}

export function validateVendorTopCategoriesByClickResponse(data) {
  const parsed = vendorTopCategoriesByClickResponseSchema.safeParse(data);
  if (!parsed.success) {
    const message = `Invalid vendor top-categories response: ${parsed.error.message}`;
    dispatchValidationError({ message, issues: parsed.error.issues, raw: parsed.error });
    throw new ValidationError(message, { issues: parsed.error.issues, raw: parsed.error });
  }
  return parsed.data;
}
